/**
 * API Exception Testing Utility
 *
 * This module provides functions to test the client-side error handling
 * by triggering various exception types from the server.
 *
 * Only available to Cyncly staff members.
 */

import { getApi } from './api-injector';

/**
 * Exception types supported by the TestException API endpoint
 */
export enum TestExceptionType {
  /** Generic exception with nested inner exceptions (500) */
  Generic = 0,
  /** Unauthenticated error (401) */
  Authentication = 1,
  /** Unauthorized/forbidden error (403) */
  Authorization = 2,
  /** Precondition failed - user already connected (412) */
  AlreadyConnected = 3,
  /** Database migration transient error (503) */
  MigrationError = 4,
  /** Database transient error - retry limit exceeded (503) */
  RetryLimitExceeded = 5,
  /** Validation failure with multiple field errors (422) */
  Validation = 6,
  /** Resource not found during modify operation (404) */
  ModifyNotFound = 7,
  /** Legacy ServiceResponse error conversion (500) */
  ServiceResponse = 8,
  /** HTTP request error - uses httpStatusCode parameter */
  HttpRequest = 9,
  /** Deeply nested exception chain - 5 levels (500) */
  DeeplyNested = 10,
  /** AggregateException with multiple inner exceptions (500) */
  Aggregate = 11
}

/**
 * Human-readable labels for each exception type
 */
export const TestExceptionTypeLabels: Record<TestExceptionType, string> = {
  [TestExceptionType.Generic]: 'Generic Exception (500)',
  [TestExceptionType.Authentication]: 'Authentication (401)',
  [TestExceptionType.Authorization]: 'Authorization (403)',
  [TestExceptionType.AlreadyConnected]: 'Already Connected (412)',
  [TestExceptionType.MigrationError]: 'Migration Error (503)',
  [TestExceptionType.RetryLimitExceeded]: 'Retry Limit Exceeded (503)',
  [TestExceptionType.Validation]: 'Validation Error (422)',
  [TestExceptionType.ModifyNotFound]: 'Modify Not Found (404)',
  [TestExceptionType.ServiceResponse]: 'Service Response (500)',
  [TestExceptionType.HttpRequest]: 'HTTP Request (custom)',
  [TestExceptionType.DeeplyNested]: 'Deeply Nested (500)',
  [TestExceptionType.Aggregate]: 'Aggregate (500)'
};

/**
 * Descriptions for each exception type to help understand what to expect
 */
export const TestExceptionTypeDescriptions: Record<TestExceptionType, string> = {
  [TestExceptionType.Generic]: 'Generic exception with nested inner exceptions',
  [TestExceptionType.Authentication]: 'Unauthenticated error - tests 401 handling',
  [TestExceptionType.Authorization]: 'Unauthorized/forbidden error - tests 403 handling',
  [TestExceptionType.AlreadyConnected]: 'User already connected from another session',
  [TestExceptionType.MigrationError]: 'Database migration transient error',
  [TestExceptionType.RetryLimitExceeded]: 'Database transient error after retry limit',
  [TestExceptionType.Validation]: 'Validation failure with multiple field errors',
  [TestExceptionType.ModifyNotFound]: 'Resource not found during modify operation',
  [TestExceptionType.ServiceResponse]: 'Legacy ServiceResponse error conversion',
  [TestExceptionType.HttpRequest]: 'HTTP request error with custom status code',
  [TestExceptionType.DeeplyNested]: '5-level deep exception chain',
  [TestExceptionType.Aggregate]: 'AggregateException with 4 inner exceptions'
};

/**
 * Triggers a test exception from the server API.
 * This is useful for testing client-side error handling.
 *
 * @param exceptionType The type of exception to trigger
 * @param customMessage Optional custom message for the exception
 * @param httpStatusCode Optional HTTP status code (only used with HttpRequest type)
 * @returns Promise that rejects with the error (as expected behavior)
 */
export async function triggerTestException(
  exceptionType: TestExceptionType = TestExceptionType.Generic,
  customMessage?: string,
  httpStatusCode?: number
): Promise<void> {
  const api = getApi();
  const params = new URLSearchParams();
  params.append('exceptionType', exceptionType.toString());
  if (customMessage) {
    params.append('customMessage', customMessage);
  }
  if (httpStatusCode !== undefined && exceptionType === TestExceptionType.HttpRequest) {
    params.append('httpStatusCode', httpStatusCode.toString());
  }

  const path = `api/Test/Exception?${params.toString()}`;

  // Use the API's post method which will go through the normal error handling pipeline
  await api.post<unknown>(path, {});
}

/**
 * All exception types as an array for iteration
 */
export const AllTestExceptionTypes: TestExceptionType[] = [
  TestExceptionType.Generic,
  TestExceptionType.Authentication,
  TestExceptionType.Authorization,
  TestExceptionType.AlreadyConnected,
  TestExceptionType.MigrationError,
  TestExceptionType.RetryLimitExceeded,
  TestExceptionType.Validation,
  TestExceptionType.ModifyNotFound,
  TestExceptionType.ServiceResponse,
  TestExceptionType.HttpRequest,
  TestExceptionType.DeeplyNested,
  TestExceptionType.Aggregate
];
